import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { CINIMAGE_LOGO_NAV, NAV_LINKS, LOGO_COLOR_NAV, LOGO_WHITE } from "../../data/content";

export const Nav = ({ onContact }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      data-testid="site-header"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
      className="fixed top-0 left-0 right-0 z-50"
    >
      <div
        className={`mx-auto flex items-center justify-between px-6 md:px-10 transition-[padding,background-color,backdrop-filter,border-color] duration-500 ${
          scrolled
            ? "py-3 bg-black/40 backdrop-blur-2xl border-b border-white/10"
            : "py-6 bg-transparent border-b border-transparent"
        }`}
      >
        <a
          href="#top"
          data-testid="nav-logo"
          aria-label="OORU by Cinimage"
          className="flex items-center gap-2 md:gap-3"
        >
          <img
            src={scrolled ? LOGO_WHITE : LOGO_COLOR_NAV}
            alt="OORU"
            data-testid="nav-logo-image"
            className="h-6 md:h-7 w-auto transition-[filter,opacity] duration-500"
          />
          <span
            data-testid="nav-logo-by-text"
            className="font-body text-[10px] md:text-xs uppercase tracking-[0.18em] text-white/45"
          >
            by
          </span>
          <img
            src={CINIMAGE_LOGO_NAV}
            alt="Cinimage"
            data-testid="nav-cinimage-logo-image"
            className="h-4 md:h-5 w-auto opacity-85 transition-opacity duration-500"
          />
        </a>

        <nav className="hidden lg:flex items-center gap-9">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className="font-body text-sm text-neutral-400 hover:text-white transition-colors duration-300"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            onClick={onContact}
            data-testid="nav-contact-button"
            className="hidden sm:inline-flex items-center rounded-full bg-white text-black font-body font-medium text-sm px-5 py-2.5 hover:bg-[#ff4500] hover:text-white transition-[transform,background-color,color,box-shadow] duration-300 hover:-translate-y-0.5 hover:shadow-[0_14px_34px_-12px_rgba(255,69,0,0.55)]"
          >
            Partner With OORU
          </button>
          <button
            onClick={() => setOpen((v) => !v)}
            data-testid="nav-mobile-toggle"
            className="lg:hidden text-white p-2"
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="lg:hidden overflow-hidden bg-black/90 backdrop-blur-2xl border-b border-white/10"
          >
            <div className="flex flex-col px-6 py-6 gap-5">
              {NAV_LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                  className="font-display text-2xl text-neutral-200"
                >
                  {l.label}
                </a>
              ))}
              <button
                onClick={() => {
                  setOpen(false);
                  onContact();
                }}
                data-testid="nav-mobile-contact-button"
                className="mt-2 rounded-full bg-[#ff4500] text-white font-body font-medium px-6 py-3 self-start"
              >
                Partner With OORU
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
